// Results on the Front Desk: the SAME report as Analítica (GET /orgs/:orgId/analytics/report, same query and
// cache key, so the numbers are identical), reduced to what a first glance needs. Only ads served through a Tag —
// demo links are never added; no media spend (InteractAds does not measure it); the provisional visibility
// criterion is not presented as viewability. Everything is filtered by the server to what the person may see.
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BarChart3 } from "lucide-react";
import { useMemo, useState } from "react";
import { api, call } from "@/api/client";
import { usePaged } from "@/api/hooks";
import type { AnalyticsReport } from "@/api/types";
import { ApiProblem } from "@/api/errors";
import { Button } from "@/ui/button";
import { NativeSelect } from "@/ui/input";
import { Callout, Section, Skeleton } from "@/ui/layout";
import { Kpi } from "../analytics/analytics-page";
import { BarList, DailyChart, fillDays } from "../analytics/charts";
import { browserOffsetMinutes, formatNumber, formatRate, localDay } from "../analytics/format";
import { HierarchyFilters, type HierarchyValue } from "../common/hierarchy-filters";

const PERIODS = [
  { id: "7", label: "Últimos 7 días", days: 7 },
  { id: "30", label: "Últimos 30 días", days: 30 },
  { id: "90", label: "Últimos 90 días", days: 90 },
] as const;

export function FrontDeskResults({ orgId }: { orgId: string }) {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]["id"]>("30");
  const [scope, setScope] = useState<HierarchyValue>({ clientId: "", brandId: "", campaignId: "" });
  const days = PERIODS.find((p) => p.id === period)?.days ?? 30;
  // Built exactly like Analítica's query (same keys, same order): one cache, one set of numbers.
  const query = useMemo(
    () => ({
      from: localDay(days - 1),
      to: localDay(0),
      tz: browserOffsetMinutes(),
      ...Object.fromEntries(Object.entries(scope).filter(([, v]) => v !== "")),
    }),
    [days, scope]
  );
  const report = useQuery({
    queryKey: ["analytics", orgId, query],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/analytics/report", { params: { path: { orgId }, query: query as never } })
      ) as Promise<AnalyticsReport>,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  // Live now: creatives with a version served by at least one Distribution (the library's own count).
  const live = usePaged(["org", orgId, "creative-library", "live", scope], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/creatives", {
        params: {
          path: { orgId },
          query: {
            limit: 100,
            published: true,
            ...(scope.clientId ? { clientId: scope.clientId } : {}),
            ...(scope.brandId ? { brandId: scope.brandId } : {}),
            ...(scope.campaignId ? { campaignId: scope.campaignId } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      })
    )
  );
  // A role without access to results simply does not see this section.
  if (report.error instanceof ApiProblem && (report.error.status === 403 || report.error.status === 404)) return null;

  const data = report.data;
  const t = data?.totals;
  const analyticsLink = `/o/${orgId}/analytics`;
  const liveCount = live.hasNextPage ? `${formatNumber(live.items.length)}+` : formatNumber(live.items.length);
  return (
    <Section
      title="Resultados de tus anuncios"
      description="Medidos por el Ad Server de InteractAds en los anuncios servidos por tu Tag. Las demostraciones no se suman."
      actions={
        <Button asChild size="sm" variant="secondary">
          <Link to={analyticsLink} search={scope.campaignId ? { campaignId: scope.campaignId } : {}}>
            <BarChart3 aria-hidden="true" /> Ver reporte completo
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4" data-testid="front-desk-results">
        <div
          className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
          role="search"
          aria-label="Filtros de resultados"
        >
          <NativeSelect
            aria-label="Período"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value as typeof period);
            }}
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </NativeSelect>
          <HierarchyFilters orgId={orgId} value={scope} onChange={setScope} />
        </div>

        {report.error ? (
          <Callout tone="warning" title="No pudimos cargar los resultados">
            Inténtalo de nuevo en unos minutos o abre el reporte completo.
          </Callout>
        ) : t?.served === 0 ? (
          // No ad served in the period: one clear message instead of zeros and empty charts.
          <Callout tone="info" title="Aún no hay resultados en este período">
            Cuando publiques un creativo y uses su Tag en tu plataforma publicitaria, aquí verás impresiones, clics e
            interacciones.
          </Callout>
        ) : !t ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="front-desk-kpis">
              <Kpi
                label="Impresiones"
                value={formatNumber(t.impressions)}
                hint="Anuncios presentados y aceptados por el Ad Server."
                primary
              />
              <Kpi label="Clics" value={formatNumber(t.clicks)} hint="Clics hacia la página del anunciante." />
              <Kpi label="CTR" value={formatRate(t.ctr)} hint="Clics ÷ impresiones." />
              <Kpi
                label="Engagements"
                value={formatNumber(t.interactions)}
                hint="Toques, respuestas, controles y clics de las personas."
              />
              <Kpi
                label="Engagement Rate"
                value={formatRate(t.engagementRate)}
                hint="Engagements ÷ impresiones. Puede superar el 100 %: en un anuncio se interactúa varias veces."
              />
              <Kpi
                label="Creativos publicados"
                value={live.isLoading ? "…" : liveCount}
                hint="Entregando ahora en al menos una distribución."
              />
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <DailyChart
                title="Impresiones"
                total={t.impressions}
                points={fillDays(
                  data.period.from,
                  data.period.to,
                  data.daily.map((d) => ({ day: d.day, value: d.impressions }))
                )}
              />
              <DailyChart
                title="Clics"
                total={t.clicks}
                points={fillDays(
                  data.period.from,
                  data.period.to,
                  data.daily.map((d) => ({ day: d.day, value: d.clicks }))
                )}
              />
              <DailyChart
                title="Engagements"
                total={t.interactions}
                points={fillDays(
                  data.period.from,
                  data.period.to,
                  data.daily.map((d) => ({ day: d.day, value: d.interactions }))
                )}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid min-w-0 content-start gap-2">
                <h3 className="text-sm font-semibold">Creativos con más impresiones</h3>
                <BarList
                  label="Impresiones por creativo"
                  empty="Sin impresiones en el período."
                  data={[...data.byCreative]
                    .filter((c) => c.metrics.impressions > 0)
                    .sort((a, b) => b.metrics.impressions - a.metrics.impressions)
                    .slice(0, 5)
                    .map((c) => ({
                      key: c.id,
                      label: c.name,
                      value: c.metrics.impressions,
                      display: `${formatNumber(c.metrics.impressions)} · CTR ${formatRate(c.metrics.ctr)}`,
                    }))}
                />
              </div>
              <div className="grid min-w-0 content-start gap-2">
                <h3 className="text-sm font-semibold">Campañas en ejecución</h3>
                <BarList
                  label="Impresiones por campaña"
                  empty="Ninguna campaña con impresiones en el período."
                  data={[...data.byCampaign]
                    .filter((c) => c.metrics.served > 0)
                    .sort((a, b) => b.metrics.impressions - a.metrics.impressions)
                    .slice(0, 5)
                    .map((c) => ({
                      key: c.id,
                      label: c.name,
                      value: c.metrics.impressions,
                      display: `${formatNumber(c.metrics.impressions)} · CTR ${formatRate(c.metrics.ctr)}`,
                    }))}
                />
              </div>
            </div>
            <Link to={analyticsLink} className="flex items-center gap-1 text-sm text-brand hover:underline">
              Ver todos los indicadores, juegos, video y encuestas en Analítica{" "}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </>
        )}
      </div>
    </Section>
  );
}
